import React, { useContext, useMemo } from 'react';
import CustomTag from '@components/CustomTag';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { ServiceRequestData } from '@services/types/Relationship/MyCalls/types';
import { dateFormat } from '@utils/formatters';
import { Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';

import * as S from './styles';

interface RequestDetailsProps {
  request?: ServiceRequestData;
}

const RequestDetails = ({ request }: RequestDetailsProps) => {
  const { t } = useTranslation('service-requests');
  const { keycloak } = useContext(KeycloakContext);

  const customerType = keycloak.idTokenParsed?.customerType;

  const requestDescriptions = useMemo(
    () => [
      {
        label: t('protocol'),
        value: request?.protocol,
        showItem: request?.protocol,
      },
      {
        label: t('assetNumber'),
        value: request?.asset?.assetNumber,
        showItem: request?.asset?.assetNumber,
      },
      {
        label: t('customer'),
        value: request?.customer?.name,
        showItem: customerType === 'ROOT',
      },
      {
        label: t('requester'),
        value: request?.createdBy ?? '',
        showItem: request?.createdBy,
      },
      {
        label: t('openingDate'),
        value: dateFormat(request?.createdAt ?? ''),
        showItem: request?.createdAt,
      },
      {
        label: t('closingDate'),
        value: dateFormat(request?.finishedAt || ''),
        showItem: request?.finishedAt,
      },
    ],
    [
      t,
      request?.protocol,
      request?.asset?.assetNumber,
      request?.customer?.name,
      request?.createdBy,
      request?.createdAt,
      request?.finishedAt,
      customerType,
    ],
  );

  const requestSrTypesOptions = useMemo(
    () =>
      [
        request?.type1?.name,
        request?.type2?.name,
        request?.type3?.name,
        request?.type4?.name,
      ]
        .filter(srTypeName => srTypeName)
        .map(
          srTypeName =>
            srTypeName &&
            t(`types.${srTypeName.toLowerCase()?.replaceAll(' ', '')}`),
        ),
    [request?.type1, request?.type2, request?.type3, request?.type4, t],
  );

  return (
    <S.RequestDetailsCard>
      <S.RequestDetailsTypes>
        {requestSrTypesOptions?.map(srType => (
          <CustomTag key={srType}>{srType?.toUpperCase()}</CustomTag>
        ))}
      </S.RequestDetailsTypes>
      <Descriptions
        layout="vertical"
        column={{ lg: 3, sm: 1, xs: 1 }}
        style={{
          padding: 8,
          paddingBottom: 0,
        }}
      >
        {requestDescriptions?.map(
          ({ label, value, showItem }) =>
            showItem && (
              <Descriptions.Item key={label} label={label}>
                {value}
              </Descriptions.Item>
            ),
        )}
      </Descriptions>
    </S.RequestDetailsCard>
  );
};

export default RequestDetails;
